import { useState, useEffect, useCallback } from 'react'
import { customerData } from '../../utilities/CustomerData'
import { refreshAccessToken } from '../../utilities/extensions'
import { apiURL } from '../../utilities/URLs'
import { useNavigate } from 'react-router-dom'
import { Autocomplete, useLoadScript } from '@react-google-maps/api'

const libraries = ['places']
const ESIGN_ORIGIN = 'https://www.inkavella.com'

export default function ReauthSign8821() {
  const navigate = useNavigate()
  const leadId = customerData.customerLeadData.leadId
  const SESSION_CACHE_KEY = `pixelink_session_${leadId}`

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [street, setStreet] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [zip, setZip] = useState('')
  const [clientSSN, setClientSSN] = useState('')
  const [signingUrl, setSigningUrl] = useState('')
  const [loadingEmbed, setLoadingEmbed] = useState(false)
  const [signingComplete, setSigningComplete] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [autocomplete, setAutocomplete] = useState(null)

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries
  })

  const handleCompleteReauth = useCallback(async (retryCount = 0) => {
    setSubmitting(true)
    try {
      const response = await fetch(`${apiURL}/api/completeReauth8821?leadId=${encodeURIComponent(customerData.customerLeadData.leadId)}`, {
        method: 'POST',
        headers: { 'Authorization': customerData.accessToken }
      })
      const data = await response.json()

      if (data.response?.retryable && retryCount < 8) {
        await new Promise(r => setTimeout(r, 1500))
        return await handleCompleteReauth(retryCount + 1)
      }

      if (data.response.error) {
        if (data.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? handleCompleteReauth(0) : navigate('/', { state: { expired: true } })
        } else
          setError(data.response.error)
      } else {
        setSubmitting(false)
      }
    } catch (err) {
      setError('An error occurred. Please try again.')
    }
    setSubmitting(false)
  }, [navigate])

  useEffect(() => {
    // Check sessionStorage for a cached signing URL
    const cached = sessionStorage.getItem(SESSION_CACHE_KEY)
    if (cached) setSigningUrl(cached)

    const handleMessage = (event) => {
      if (event.origin !== ESIGN_ORIGIN) return
      if (event.data?.type === 'esign_complete') {
        setSigningComplete(true)
        sessionStorage.removeItem(SESSION_CACHE_KEY)
        handleCompleteReauth()
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [handleCompleteReauth])

  const handleLoad = (autocompleteInstance) => {
    setAutocomplete(autocompleteInstance)
  }

  const handlePlaceChanged = () => {
    const place = autocomplete.getPlace()
    if (place && place.address_components) {
      const getComponent = (type, useShort = false) => {
        const comp = place.address_components.find(c => c.types.includes(type))
        return comp ? (useShort ? comp.short_name : comp.long_name) : ''
      }
      setStreet(`${getComponent('street_number')} ${getComponent('route')}`.trim())
      setCity(getComponent('locality'))
      setState(getComponent('administrative_area_level_1', true))
      setZip(getComponent('postal_code'))
    }
  }

  const handleSSNChange = (e) => {
    let value = e.target.value.replace(/\D/g, '')
    if (value.length > 9) value = value.slice(0, 9)
    if (value.length > 5) value = `${value.slice(0, 3)}-${value.slice(3, 5)}-${value.slice(5)}`
    else if (value.length > 3) value = `${value.slice(0, 3)}-${value.slice(3)}`
    setClientSSN(value)
  }

  const isFormValid = firstName && lastName && street && city && state && zip && clientSSN.length === 11

  const handleCreateSession = async () => {
    setLoadingEmbed(true)
    setError('')
    try {
      const form = new FormData()
      form.append('firstName', firstName)
      form.append('lastName', lastName)
      form.append('address', `${street}, ${city}, ${state} ${zip}`)
      form.append('ssn', clientSSN)

      const response = await fetch(`${apiURL}/api/getReauth8821EmbedURL?leadId=${encodeURIComponent(customerData.customerLeadData.leadId)}`, {
        method: 'POST',
        headers: { 'Authorization': customerData.accessToken },
        body: form
      })
      const data = await response.json()

      if (data.response.error) {
        if (data.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? handleCreateSession() : navigate('/', { state: { expired: true } })
        } else
          setError(data.response.error)
      } else {
        setSigningUrl(data.response.signingUrl)
        sessionStorage.setItem(SESSION_CACHE_KEY, data.response.signingUrl)
      }
    } catch (err) {
      setError('An error occurred. Please try again.')
    }
    setLoadingEmbed(false)
  }

  return (
    <div className='flex flex-col items-center justify-center min-h-screen bg-gray-50 font-mont p-4'>
      <div className='w-full max-w-5xl'>
        <div className='shadow-md p-4 rounded-xl w-full flex flex-col items-center bg-[#5186C0] text-white mb-4'>
          <p className='font-bold text-2xl'>Step 3: Sign Form 8821</p>
          <p className='text-center mt-2'>Please provide your information and sign the form to authorize us to access your tax information.</p>
        </div>
        <div className='shadow-md p-6 rounded-xl w-full flex flex-col items-center bg-white gap-4'>
          {!signingComplete ? (
            <>
              {!signingUrl ? (
                <>
                  <p className='text-[#646464] text-center'>Please provide the following information to prepare your Form 8821.</p>
                  <p className='text-[#5186C0] font-semibold text-center mt-2'>Please enter your name exactly as it appears on your most recent IRS tax return.</p>
                  {isLoaded ? (
                    <div className='flex flex-col gap-3 w-full mt-2'>
                      <div className='flex flex-row gap-3'>
                        <label className='text-sm font-semibold text-[#646464] flex-1'>
                          First Name
                          <input
                            type='text'
                            placeholder='First Name'
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            className='mt-1 w-full border border-gray-300 rounded-md p-2 font-normal'
                          />
                        </label>
                        <label className='text-sm font-semibold text-[#646464] flex-1'>
                          Last Name
                          <input
                            type='text'
                            placeholder='Last Name'
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            className='mt-1 w-full border border-gray-300 rounded-md p-2 font-normal'
                          />
                        </label>
                      </div>
                      <label className='text-sm font-semibold text-[#646464]'>
                        Street Address
                        <Autocomplete onLoad={handleLoad} onPlaceChanged={handlePlaceChanged}>
                          <input
                            type='text'
                            placeholder='Start typing your address...'
                            value={street}
                            onChange={(e) => setStreet(e.target.value)}
                            className='mt-1 w-full border border-gray-300 rounded-md p-2 font-normal'
                          />
                        </Autocomplete>
                      </label>
                      <div className='flex flex-row gap-3'>
                        <label className='text-sm font-semibold text-[#646464] flex-1'>
                          City
                          <input
                            type='text'
                            placeholder='City'
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            className='mt-1 w-full border border-gray-300 rounded-md p-2 font-normal'
                          />
                        </label>
                        <label className='text-sm font-semibold text-[#646464] w-24'>
                          State
                          <input
                            type='text'
                            placeholder='TX'
                            maxLength={2}
                            value={state}
                            onChange={(e) => setState(e.target.value.toUpperCase())}
                            className='mt-1 w-full border border-gray-300 rounded-md p-2 font-normal'
                          />
                        </label>
                        <label className='text-sm font-semibold text-[#646464] w-28'>
                          ZIP Code
                          <input
                            type='text'
                            placeholder='12345'
                            maxLength={5}
                            value={zip}
                            onChange={(e) => setZip(e.target.value.replace(/\D/g, ''))}
                            className='mt-1 w-full border border-gray-300 rounded-md p-2 font-normal'
                          />
                        </label>
                      </div>
                      <label className='text-sm font-semibold text-[#646464]'>
                        Social Security Number (SSN)
                        <input
                          type='text'
                          placeholder='XXX-XX-XXXX'
                          value={clientSSN}
                          onChange={handleSSNChange}
                          maxLength={11}
                          className='mt-1 w-full border border-gray-300 rounded-md p-2 font-normal'
                        />
                      </label>
                    </div>
                  ) : (
                    <div className='flex flex-row items-center justify-center gap-2 w-full'>
                      <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] h-8 w-8'></div>
                      <p className='text-md text-center'>Loading...</p>
                    </div>
                  )}
                  <p className='text-xs text-gray-400 italic text-center'>Your address and SSN are not stored. This information is only used to prepare your Form 8821.</p>
                  {error && <p className='text-red-500 text-sm'>{error}</p>}
                  <button
                    onClick={handleCreateSession}
                    disabled={!isFormValid || loadingEmbed}
                    className={`mt-2 px-6 py-3 rounded-lg text-white font-semibold transition-all w-full ${isFormValid && !loadingEmbed ? 'bg-[#FF8C00] hover:bg-[#E67E00]' : 'bg-gray-400 cursor-not-allowed'}`}
                  >
                    {loadingEmbed ? 'Preparing Document...' : 'Continue to Sign'}
                  </button>
                  {loadingEmbed && (
                    <div className='flex flex-row items-center justify-center gap-2 w-full'>
                      <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] h-8 w-8'></div>
                      <p className='text-md text-center'>Preparing your document...</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <p className='text-[#646464] text-center'>Please review and sign your Form 8821 below.</p>
                  <iframe
                    src={signingUrl}
                    style={{ width: '100%', height: '700px', border: 'none', borderRadius: '8px' }}
                    allow='fullscreen'
                    title='E-Sign Form 8821'
                  />
                </>
              )}
            </>
          ) : (
            <>
              {submitting ? (
                <div className='flex flex-col items-center justify-center gap-4 py-8'>
                  <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] h-10 w-10'></div>
                  <p className='text-[#646464] text-center'>Completing your reauthorization...</p>
                </div>
              ) : (
                <div className='flex flex-col items-center justify-center gap-4 py-8'>
                  <div className='flex items-center justify-center w-16 h-16 rounded-full bg-green-100'>
                    <svg className='w-8 h-8 text-green-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' /></svg>
                  </div>
                  <p className='text-[#646464] text-center text-lg font-semibold'>Reauthorization Complete!</p>
                  <p className='text-[#646464] text-center text-sm'>Thank you for completing all steps. Your account has been reauthorized.</p>
                  {error && <p className='text-red-500 text-sm'>{error}</p>}
                  <button
                    onClick={() => window.location.href = '/dashboard'}
                    className='px-6 py-3 rounded-lg text-white font-semibold transition-all bg-[#5186C0] hover:bg-[#4070A0]'
                  >
                    Return to Dashboard
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        <button
          onClick={() => navigate('/dashboard')}
          className='mt-4 text-[#5186C0] hover:underline text-sm w-full text-center'
        >
          ← Back to Dashboard
        </button>
      </div>
    </div>
  )
}
